import {
  compareDocuments,
  buildDiffArtefacts,
  validateVersionPair,
  renderOverlay,
  sheetFrame,
  ExtractionError,
  VersionPairError,
  DEFAULT_MATCHER_PROFILE,
  type DiffArtefacts,
  type DiffIdentity,
  type DiffResult,
  type DrawingPage,
  type VersionRef,
} from '@drawing2d/core';
import { runPipeline, type RunResult } from './pipeline.js';

/**
 * The comparison pipeline, run entirely in the browser.
 *
 * Two drawings go through the same conversion the single-document surface uses,
 * and only then are they compared. That order matters: a comparison is a
 * statement about two *structured* readings, so anything the extractor could not
 * read is already a quality issue on its own side rather than a change nobody
 * made.
 *
 * This module exists to hold one decision the components must not each make for
 * themselves: **what coordinate system the three views share**. Before, after
 * and diff are three drawings of the same sheet, and they register only if all
 * three are drawn into the union of both versions' page boxes. Computed here,
 * once, and handed to every view — because registration built by tuning each
 * view separately is registration that drifts the first time a page size
 * changes, and the failure looks exactly like the drawing having moved.
 *
 * The rule itself lives in the engine, as `sheetFrame`, and is read from there
 * rather than restated: the difference view is drawn by the engine and the other
 * two are drawn here, so a second copy of the rule would be a second rule.
 */

/**
 * One pair of aligned sheets, ready to draw.
 *
 * A comparison sheet is a *pair*, not a page: after a sheet is inserted, base
 * page 1 answers to compare page 2. So this carries both page indices, either of
 * which may be null, and never a single page number.
 */
export interface ComparisonSheet {
  /** Index into `diff.pageAlignment`, and the key every artefact is named by. */
  index: number;
  /** Null when the after version gained this sheet. */
  basePageIndex: number | null;
  /** Null when the after version lost it. */
  comparePageIndex: number | null;
  basePage: DrawingPage | null;
  comparePage: DrawingPage | null;
  /** The union of both page boxes: the one coordinate system all three views use. */
  frame: { widthPt: number; heightPt: number };
  /** The before overlay, drawn into the shared frame. Null when the sheet is new. */
  before: string | null;
  /** The after overlay, drawn into the shared frame. Null when the sheet was removed. */
  after: string | null;
  diff: string;
}

export interface Comparison {
  base: RunResult;
  compare: RunResult;
  diff: DiffResult;
  identity: DiffIdentity;
  artefacts: DiffArtefacts;
  sheets: ComparisonSheet[];
  elapsedMs: number;
}

export interface CompareFailure {
  code: string;
  message: string;
}

/**
 * Who is being compared.
 *
 * There is no server here and no tenant boundary to enforce, so the identity is
 * asserted by the person who put one drawing in the before slot and the other in
 * the after slot. It is still built through `validateVersionPair`, because that
 * is where the contract-version and artefact rules live, and a surface that
 * skipped them would be demonstrating something the product does not do.
 */
const TENANT = 'local';
const DOCUMENT = 'browser-comparison';

export async function runComparison(
  beforeBytes: Uint8Array,
  afterBytes: Uint8Array,
): Promise<Comparison> {
  const started = performance.now();

  const base = await runPipeline(beforeBytes, null);
  const compare = await runPipeline(afterBytes, null);

  const identity = validateVersionPair(
    { ref: refFor('before', base), document: base.document },
    { ref: refFor('after', compare), document: compare.document },
    DEFAULT_MATCHER_PROFILE,
  );

  const diff = compareDocuments(base.document, compare.document, DEFAULT_MATCHER_PROFILE);
  const artefacts = await buildDiffArtefacts(base.document, compare.document, diff, identity);

  const sheets = diff.pageAlignment.map((pair, index): ComparisonSheet => {
    const basePage = pageAt(base, pair.basePageIndex);
    const comparePage = pageAt(compare, pair.comparePageIndex);
    // The union of both page boxes, never a rescale of one onto the other:
    // rescaling would turn a change of paper size into apparent movement of
    // every entity on the sheet. The engine owns the rule; this reads it.
    const frame = sheetFrame(base.document, compare.document, diff, index);
    return {
      index,
      basePageIndex: pair.basePageIndex,
      comparePageIndex: pair.comparePageIndex,
      basePage,
      comparePage,
      frame,
      before: basePage ? renderOverlay(base.document, basePage.index, { frame }) : null,
      after: comparePage ? renderOverlay(compare.document, comparePage.index, { frame }) : null,
      diff: artefacts.overlays[index] ?? '',
    };
  });

  return {
    base,
    compare,
    diff,
    identity,
    artefacts,
    sheets,
    elapsedMs: Math.round(performance.now() - started),
  };
}

function pageAt(result: RunResult, index: number | null): DrawingPage | null {
  if (index === null) return null;
  return result.document.pages.find((p) => p.index === index) ?? null;
}

/**
 * The hash of a side's `drawing.json`, which is what makes a stored comparison
 * idempotent. An absent one is left empty rather than invented, so the pair
 * validator refuses the comparison instead of this module hiding the gap.
 */
function refFor(versionId: string, result: RunResult): VersionRef {
  return {
    tenantId: TENANT,
    documentId: DOCUMENT,
    documentVersionId: versionId,
    artefactSha256:
      result.artefacts.manifests.find((m) => m.storageKey === 'drawing.json')?.sha256 ?? '',
  };
}

/**
 * The content behind one comparison artefact's download button.
 *
 * Every key the manifest can carry is answered here. A key that falls through
 * returns an empty string, and an empty string is a zero-byte download that
 * looks like a working button — so a new artefact means a new branch here, and
 * the browser test downloads one to keep that honest.
 */
export function diffContentFor(comparison: Comparison, storageKey: string): string {
  const name = basenameOf(storageKey);
  if (name === 'diff.json') return comparison.artefacts.diffJson;
  if (name === 'diff-metrics.json') return comparison.artefacts.metrics;
  const sheet = /^diff-overlay(?:\.s(\d+))?\.svg$/.exec(name);
  if (sheet) {
    const index = sheet[1] === undefined ? 0 : Number(sheet[1]) - 1;
    return comparison.artefacts.overlays[index] ?? '';
  }
  return '';
}

/**
 * The storage key is a path carrying both artefact hashes and the matcher
 * version, which is what a stored comparison is filed under. A person looking
 * at a list of downloads wants the file's name, so the panel shows the last
 * segment and the full key stays in the manifest.
 */
export function basenameOf(storageKey: string): string {
  return storageKey.slice(storageKey.lastIndexOf('/') + 1);
}

export function describeCompareFailure(err: unknown): CompareFailure {
  if (err instanceof VersionPairError) return { code: err.code, message: err.message };
  if (err instanceof ExtractionError) return { code: err.code, message: err.message };
  return { code: 'UNEXPECTED', message: err instanceof Error ? err.message : String(err) };
}
